<!-- Derived from vitepress/src/client/theme-default/components/VPLocalSearchBox.vue -->
<template>
  <teleport to="body">
    <div
      ref="el"
      role="button"
      :aria-owns="results?.length ? 'localsearch-list' : undefined"
      aria-expanded="true"
      aria-haspopup="listbox"
      aria-labelledby="localsearch-label"
      class="yb-doc-search"
      data-test-id="docsearch-root"
    >
      <div class="backdrop" @click="$emit('close')" />

      <div class="shell">
        <form
          class="search-bar"
          @pointerup="onSearchBarClick($event)"
          @submit.prevent=""
        >
          <label
            id="localsearch-label"
            :title="buttonText"
            for="localsearch-input"
          >
            <span aria-hidden="true" class="vpi-search search-icon local-search-icon" />
          </label>
          <div class="search-actions before">
            <button
              class="back-button"
              :title="translate('modal.backButtonTitle')"
              @click="$emit('close')"
            >
              <span class="vpi-arrow-left local-search-icon" />
            </button>
          </div>
          <input
            id="localsearch-input"
            ref="searchInput"
            v-model="filterText"
            :aria-activedescendant="selectedIndex > -1 ? ('localsearch-item-' + selectedIndex) : undefined"
            aria-autocomplete="both"
            :aria-controls="results?.length ? 'localsearch-list' : undefined"
            aria-labelledby="localsearch-label"
            autocapitalize="off"
            autocomplete="off"
            autocorrect="off"
            class="search-input yb-input"
            enterkeyhint="go"
            maxlength="64"
            :placeholder="buttonText"
            spellcheck="false"
            type="search"
            data-test-id="docsearch-search-input"
          >
          <div class="search-actions">
            <button
              v-if="!disableDetailedView"
              class="toggle-layout-button"
              type="button"
              :class="{ 'detailed-list': showDetailedList }"
              :title="translate('modal.displayDetails')"
              data-test-id="docsearch-button-detailed"
              @click="
                selectedIndex > -1 && (showDetailedList = !showDetailedList)
              "
            >
              <span class="vpi-layout-list local-search-icon" />
            </button>

            <button
              class="clear-button"
              type="reset"
              :disabled="disableReset"
              :title="translate('modal.resetButtonTitle')"
              data-test-id="docsearch-button-clear"
              @click="resetSearch"
            >
              <span class="vpi-delete local-search-icon" />
            </button>
          </div>
        </form>

        <ul
          :id="results?.length ? 'localsearch-list' : undefined"
          ref="resultsEl"
          :role="results?.length ? 'listbox' : undefined"
          :aria-labelledby="results?.length ? 'localsearch-label' : undefined"
          class="results"
          data-test-id="docsearch-search-list"
          @mousemove="onMouseMove"
        >
          <li
            v-for="(p, index) in results"
            :id="'localsearch-item-' + index"
            :key="p.id"
            :aria-selected="selectedIndex === index ? 'true' : 'false'"
            role="option"
            :data-test-id="'docsearch-search-list-item-' + index"
          >
            <a
              :href="`${docsite}${p.id}`"
              target="_blank"
              class="result"
              :class="{
                selected: selectedIndex === index
              }"
              :aria-label="[...p.titles, p.title].join(' > ')"
              :data-index="index"
              @mouseenter="!disableMouseOver && (selectedIndex = index)"
              @focusin="selectedIndex = index"
              @click="$emit('close')"
            >
              <div>
                <div class="titles">
                  <span class="title-icon">#</span>
                  <span
                    v-for="(t, index) in p.titles"
                    :key="index"
                    class="title"
                  >
                    <span class="text" :data-test-id="'docsearch-search-list-item-title-' + index" v-html="t" />
                    <span class="vpi-chevron-right local-search-icon" />
                  </span>
                  <span class="title main">
                    <span class="text" data-test-id="docsearch-search-list-item-title" v-html="p.title" />
                  </span>
                </div>

                <div v-if="showDetailedList" class="excerpt-wrapper">
                  <div v-if="p.text" class="excerpt" inert>
                    <div class="vp-doc" data-test-id="docsearch-search-list-item-content" v-html="p.text" />
                  </div>
                  <div class="excerpt-gradient-bottom" />
                  <div class="excerpt-gradient-top" />
                </div>
              </div>
            </a>
          </li>
          <li
            v-if="filterText && !results.length && enableNoResults"
            class="no-results"
            data-test-id="docsearch-search-list-no-results"
          >
            {{ translate('modal.noResultsText') }} "<strong>{{ filterText }}</strong>"
          </li>
        </ul>

        <div class="search-keyboard-shortcuts">
          <span>
            <kbd :aria-label="translate('modal.footer.navigateUpKeyAriaLabel')">
              <span class="vpi-arrow-up navigate-icon" />
            </kbd>
            <kbd :aria-label="translate('modal.footer.navigateDownKeyAriaLabel')">
              <span class="vpi-arrow-down navigate-icon" />
            </kbd>
            {{ translate('modal.footer.navigateText') }}
          </span>
          <span>
            <kbd :aria-label="translate('modal.footer.selectKeyAriaLabel')">
              <span class="vpi-corner-down-left navigate-icon" />
            </kbd>
            {{ translate('modal.footer.selectText') }}
          </span>
          <span>
            <kbd :aria-label="translate('modal.footer.closeKeyAriaLabel')">esc</kbd>
            {{ translate('modal.footer.closeText') }}
          </span>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script lang="ts" setup>
import {
  computedAsync,
  debouncedWatch,
  onKeyStroke,
  useEventListener,
  useLocalStorage,
  useScrollLock,
  useSessionStorage
} from '@vueuse/core'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import Mark from 'mark.js/src/vanilla.js'
import MiniSearch, { type SearchResult } from 'minisearch'
import {
  computed,
  markRaw,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  watch,
  watchEffect,
  type Ref
} from 'vue'
import { LRUCache } from './support/lru'
import { createSearchTranslate } from './support/translation'
import useSearch from './support/search'

const emit = defineEmits<{(e: 'close'): void}>()

const el = shallowRef<HTMLElement>()
const resultsEl = shallowRef<HTMLElement>()

/* Search */
const { searchIndexData, siteIndexData, pathToTarget, docsite, loaded, fetchOptions } = useSearch()
try {
  await loaded
} catch (e) {
  console.error('Failed to load search index:', e)
  useDialogs().error('Failed to load search index')
  emit('close')
}

interface Result {
  title: string
  titles: string[]
  text?: string
}

// TODO: how do we do this?
const dataSymbol: InjectionKey<any> = Symbol()

// TODO: less phony?
const vitePressData = {
  frontmatter: {
    value: {}
  },
  page: {
    value: {
      params: {}
    }
  }
}

const { activate } = useFocusTrap(el, {
  immediate: true,
  allowOutsideClick: true,
  clickOutsideDeactivates: true,
  escapeDeactivates: true
})
const { themeConfig } = siteIndexData.value
const searchIndex = computedAsync(async () =>
  markRaw(
    MiniSearch.loadJSON<Result>(
      searchIndexData.value,
      {
        fields: ['title', 'titles', 'text'],
        storeFields: ['title', 'titles'],
        searchOptions: {
          fuzzy: 0.2,
          prefix: true,
          boost: { title: 4, text: 2, titles: 1 },
          ...(themeConfig?.search?.provider === 'local' &&
            themeConfig?.search.options?.miniSearch?.searchOptions)
        },
        ...(themeConfig?.search?.provider === 'local' &&
          themeConfig?.search.options?.miniSearch?.options)
      }
    )
  )
)

const disableQueryPersistence = computed(() => {
  return (
    themeConfig?.search?.provider === 'local' &&
    themeConfig?.search.options?.disableQueryPersistence === true
  )
})

const filterText = disableQueryPersistence.value
  ? ref('')
  : useSessionStorage('vitepress:local-search-filter', '')

const showDetailedList = useLocalStorage(
  'YM.docsearch-detailed-list',
  themeConfig?.search?.provider === 'local' &&
    themeConfig?.search.options?.detailedView === true
)

const disableDetailedView = computed(() => {
  return (
    themeConfig?.search?.provider === 'local' &&
    (themeConfig?.search.options?.disableDetailedView === true ||
      themeConfig?.search.options?.detailedView === false)
  )
})

const localeIndex = ref<string>('root')
const buttonText = computed(() => {
  const options = themeConfig?.search?.options ?? themeConfig?.algolia

  return (
    options?.locales?.[localeIndex.value]?.translations?.button?.buttonText ||
    options?.translations?.button?.buttonText ||
    'Search'
  )
})

watchEffect(() => {
  if (disableDetailedView.value) {
    showDetailedList.value = false
  }
})

const results: Ref<(SearchResult & Result)[]> = shallowRef([])

const enableNoResults = ref(false)

watch(filterText, () => {
  enableNoResults.value = false
})

const mark = computedAsync(async () => {
  if (!resultsEl.value) { return }
  return markRaw(new Mark(resultsEl.value))
}, null)

const cache = new LRUCache<string, Map<string, string>>(16) // 16 files

debouncedWatch(
  () => [searchIndex.value, filterText.value, showDetailedList.value] as const,
  async ([index, filterTextValue, showDetailedListValue], old, onCleanup) => {
    if (old?.[0] !== index) {
      // in case of hmr
      cache.clear()
    }

    let canceled = false
    onCleanup(() => {
      canceled = true
    })

    if (!index) { return }

    // Search
    results.value = index
      .search(filterTextValue)
      .slice(0, 16) as (SearchResult & Result)[]
    enableNoResults.value = true

    // Reference rewriting
    function rewriteRelativeAttributes(el: Element, relative: string) {
      if (el.tagName?.toLowerCase() === 'a') {
        const href = el.getAttribute('href')
        if (href?.startsWith('/')) {
          el.setAttribute('href', docsite + href)
        }
        if (href?.startsWith('.')) {
          el.setAttribute('href', docsite + relative + '/' + href)
        }
      } else if (el.tagName?.toLowerCase() === 'img') {
        const src = el.getAttribute('src')
        if (src?.startsWith('/')) {
          el.setAttribute('src', docsite + src)
        }
        if (src?.startsWith('.')) {
          el.setAttribute('src', docsite + relative + '/' + src)
        }
      }
    }

    // Highlighting
    const mods = showDetailedListValue
      ? await Promise.all(results.value.map(r => fetchExcerpt(r.id)))
      : []
    if (canceled) { return }
    for (const { id, content } of mods) {
      const mapId = id.slice(0, id.indexOf('#'))
      let map = cache.get(mapId)
      if (map) { continue }
      map = new Map()
      cache.set(mapId, map)
      if (content) {
        const parser = new DOMParser()
        const doc = parser.parseFromString(content, 'text/html')
        const relative = mapId?.replace(/(.*)\/(.*)/, '$1')

        const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6')
        headings.forEach((el) => {
          const href = el.querySelector('a')?.getAttribute('href')
          const anchor = href?.startsWith('#') && href.slice(1)
          if (!anchor) { return }

          let html = ''
          let sibling = el.nextElementSibling
          while (sibling && !/^h[1-6]$/i.test(sibling.tagName)) {
            // Normalize <a> or <img> tags
            const links = sibling.querySelectorAll?.('a') ?? []
            const images = sibling.querySelectorAll?.('img') ?? [];
            [sibling, ...links, ...images].forEach((el: Element) => rewriteRelativeAttributes(el, relative))

            html += sibling.outerHTML
            sibling = sibling.nextElementSibling
          }

          map.set(anchor, html)
        })
      }
      if (canceled) { return }
    }

    const terms = new Set<string>()

    results.value = results.value.map((r) => {
      const [id, anchor] = r.id.split('#')
      const map = cache.get(id)
      const text = map?.get(anchor) ?? ''
      for (const term in r.match) {
        terms.add(term)
      }
      return { ...r, text }
    })

    await nextTick()
    if (canceled) { return }

    await new Promise((r) => {
      mark.value?.unmark({
        done: () => {
          mark.value?.markRegExp(formMarkRegex(terms), { done: r })
        }
      })
    })

    const excerpts = el.value?.querySelectorAll('.result .excerpt') ?? []
    for (const excerpt of excerpts) {
      excerpt
        .querySelector('mark[data-markjs="true"]')
        ?.scrollIntoView({ block: 'center' })
    }
    // FIXME: without this whole page scrolls to the bottom
    resultsEl.value?.firstElementChild?.scrollIntoView({ block: 'start' })
  },
  { debounce: 200, immediate: true }
)

async function fetchExcerpt(id: string) {
  const file = pathToTarget(id.slice(0, id.indexOf('#')))
  try {
    if (!file) {
      throw new Error(`Cannot find file for id: ${id}`)
    }
    const contentResponse = await fetch(file, fetchOptions)
    if (!contentResponse.ok) {
      throw new Error(`Failed to fetch content for id: ${id}, status: ${contentResponse.status}`)
    }
    return { id, content: await contentResponse.text() }
  } catch (e) {
    console.error(e)
    return { id, content: null }
  }
}

/* Search input focus */

const searchInput = ref<HTMLInputElement>()
const disableReset = computed(() => {
  return filterText.value?.length <= 0
})
function focusSearchInput(select = true) {
  searchInput.value?.focus()
  select && searchInput.value?.select()
}

onMounted(() => {
  focusSearchInput()
})

function onSearchBarClick(event: PointerEvent) {
  if (event.pointerType === 'mouse') {
    focusSearchInput()
  }
}

/* Search keyboard selection */

const selectedIndex = ref(-1)
const disableMouseOver = ref(true)

watch(results, (r) => {
  selectedIndex.value = r.length ? 0 : -1
  scrollToSelectedResult()
})

function scrollToSelectedResult() {
  nextTick(() => {
    const selectedEl = document.querySelector('.result.selected')
    selectedEl?.scrollIntoView({ block: 'nearest' })
  })
}

onKeyStroke('ArrowUp', (event) => {
  event.preventDefault()
  selectedIndex.value--
  if (selectedIndex.value < 0) {
    selectedIndex.value = results.value.length - 1
  }
  disableMouseOver.value = true
  scrollToSelectedResult()
})

onKeyStroke('ArrowDown', (event) => {
  event.preventDefault()
  selectedIndex.value++
  if (selectedIndex.value >= results.value.length) {
    selectedIndex.value = 0
  }
  disableMouseOver.value = true
  scrollToSelectedResult()
})

onKeyStroke('Enter', (e) => {
  if (e.isComposing) { return }

  if (e.target instanceof HTMLButtonElement && e.target.type !== 'submit') { return }

  const selectedPackage = results.value[selectedIndex.value]
  if (e.target instanceof HTMLInputElement && !selectedPackage) {
    e.preventDefault()
    return
  }

  if (selectedPackage) {
    window.open(`${docsite}${selectedPackage.id}`, '_blank')
    emit('close')
  }
})

onKeyStroke('Escape', () => {
  emit('close')
})

// Translations
const defaultTranslations = {
  modal: {
    displayDetails: 'Display detailed list',
    resetButtonTitle: 'Reset search',
    backButtonTitle: 'Close search',
    noResultsText: 'No results for',
    footer: {
      selectText: 'to select',
      selectKeyAriaLabel: 'enter',
      navigateText: 'to navigate',
      navigateUpKeyAriaLabel: 'up arrow',
      navigateDownKeyAriaLabel: 'down arrow',
      closeText: 'to close',
      closeKeyAriaLabel: 'escape'
    }
  }
}

const translate = createSearchTranslate(defaultTranslations)

// Back

onMounted(() => {
  // Prevents going to previous site
  window.history.pushState(null, '', null)
})

useEventListener('popstate', (event) => {
  event.preventDefault()
  emit('close')
})

/** Lock body */
const isLocked = useScrollLock(document.body)

onMounted(() => {
  nextTick(() => {
    isLocked.value = true
    nextTick().then(() => activate())
  })
})

onBeforeUnmount(() => {
  isLocked.value = false
})

function resetSearch() {
  filterText.value = ''
  nextTick().then(() => focusSearchInput(false))
}

// https://github.com/sindresorhus/escape-string-regexp/blob/ba9a4473850cb367936417e97f1f2191b7cc67dd/index.js
function escapeRegExp(str: string) {
  return str.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&').replace(/-/g, '\\x2d')
}

function formMarkRegex(terms: Set<string>) {
  return new RegExp(
    [...terms]
      .sort((a, b) => b.length - a.length)
      .map(term => `(${escapeRegExp(term)})`)
      .join('|'),
    'gi'
  )
}

function onMouseMove(e: MouseEvent) {
  if (!disableMouseOver.value) { return }
  const el = (e.target as HTMLElement)?.closest<HTMLAnchorElement>('.result')
  const index = Number.parseInt(el?.dataset.index!)
  if (index >= 0 && index !== selectedIndex.value) {
    selectedIndex.value = index
  }
  disableMouseOver.value = false
}
</script>

<style scoped lang="postcss">

.yb-doc-search {
  --vp-backdrop-bg-color: rgba(0, 0, 0, .6)
  --vp-c-bg: #ffffff;
  --vp-c-divider: #e2e2e3;
  --vp-c-brand-1: #86bd4d;
  --vp-local-search-result-border: var(--vp-c-divider);
  --vp-local-search-result-selected-border: var(--vp-c-brand-1);
  --vp-code-block-bg: #333;
  --vp-local-search-highlight-bg: #3dd68c;
  --vp-local-search-highlight-text: #ffffff;
}

.dark .yb-doc-search {
  --vp-c-bg: #1b1b1f;
  --vp-c-divider: #2e2e32;
}

.yb-doc-search {
  position: fixed;
  z-index: 100;
  inset: 0;
  display: flex;
}

.backdrop {
  position: absolute;
  inset: 0;
  background: var(--vp-backdrop-bg-color);
  transition: opacity 0.5s;
}

.shell {
  position: relative;
  padding: 12px;
  margin: 64px auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  /* background: var(--vp-local-search-bg); */
  width: min(100vw - 60px, 900px);
  height: min-content;
  max-height: min(100vh - 128px, 900px);
  border-radius: 6px;
  @apply shadow-2xl yb-body-background yb-border-content border;
}

@media (max-width: 767px) {
  .shell {
    margin: 0;
    width: 100vw;
    height: 100vh;
    max-height: none;
    border-radius: 0;
  }
}

.search-bar {
  /*
  border: 1px solid var(--vp-c-divider);
  border-radius: 4px;
  */
  display: flex;
  align-items: center;
  padding: 0 12px;
  cursor: text;
}

@media (max-width: 767px) {
  .search-bar {
    padding: 0 8px;
  }
}

.search-bar:focus-within {
  border-color: var(--vp-c-brand-1)
}

.local-search-icon {
  display: block;
  font-size: 18px;
}

.navigate-icon {
  display: block;
  font-size: 14px;
}

.search-icon {
  margin: 8px;
}

@media (max-width: 767px) {
  .search-icon {
    display: none;
  }
}

.search-input {
  padding: 6px 12px;
  font-size: inherit;
  width: 100%;
}

.search-input::-webkit-search-cancel-button {
  display: none;
}

@media (max-width: 767px) {
  .search-input {
    padding: 6px 4px;
  }
}

.search-actions {
  display: flex;
  gap: 4px;
}

@media (any-pointer: coarse) {
  .search-actions {
    gap: 8px;
  }
}

@media (min-width: 769px) {
  .search-actions.before {
    display: none;
  }
}

.search-actions button {
  padding: 8px;
}

.search-actions button:not([disabled]):hover,
.toggle-layout-button.detailed-list {
  color: var(--vp-c-brand-1);
}

.search-actions button.clear-button:disabled {
  opacity: 0.37;
}

.search-keyboard-shortcuts {
  font-size: 0.8rem;
  opacity: 75%;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  line-height: 14px;
}

.search-keyboard-shortcuts span {
  display: flex;
  align-items: center;
  gap: 4px;
}

@media (max-width: 767px) {
  .search-keyboard-shortcuts {
    display: none;
  }
}

.search-keyboard-shortcuts kbd {
  background: rgba(128, 128, 128, 0.1);
  border-radius: 4px;
  padding: 3px 6px;
  min-width: 24px;
  display: inline-block;
  text-align: center;
  vertical-align: middle;
  border: 1px solid rgba(128, 128, 128, 0.15);
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.1);
}

.results {
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.result {
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
  transition: none;
  line-height: 1rem;
  border: solid 2px var(--vp-local-search-result-border);
  outline: none;
}

.result > div {
  margin: 12px;
  width: 100%;
  overflow: hidden;
}

@media (max-width: 767px) {
  .result > div {
    margin: 8px;
  }
}

.titles {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  position: relative;
  z-index: 1001;
  padding: 2px 0;
}

.title {
  display: flex;
  align-items: center;
  gap: 4px;
}

.title.main {
  font-weight: 500;
}

.title-icon {
  opacity: 0.5;
  font-weight: 500;
  color: var(--vp-c-brand-1);
}

.title svg {
  opacity: 0.5;
}

.result.selected {
  --vp-local-search-result-bg: var(--vp-local-search-result-selected-bg);
  border-color: var(--vp-local-search-result-selected-border);
}

.excerpt-wrapper {
  position: relative;
}

.excerpt {
  opacity: 50%;
  pointer-events: none;
  max-height: 140px;
  overflow: hidden;
  position: relative;
  margin-top: 4px;
}

.result.selected .excerpt {
  opacity: 1;
}

.excerpt :deep(*) {
  font-size: 0.8rem !important;
  line-height: 130% !important;
}

.titles :deep(mark),
.excerpt :deep(mark) {
  background-color: var(--vp-local-search-highlight-bg);
  color: var(--vp-local-search-highlight-text);
  border-radius: 2px;
  padding: 0 2px;
}

.excerpt :deep(.vp-code-group) .tabs {
  display: none;
}

.excerpt :deep(.vp-code-group) div[class*='language-'] {
  border-radius: 8px !important;
}

.excerpt-gradient-bottom {
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 100%;
  height: 8px;
  background: linear-gradient(transparent, var(--vp-local-search-result-bg));
  z-index: 1000;
}

.excerpt-gradient-top {
  position: absolute;
  top: -1px;
  left: 0;
  width: 100%;
  height: 8px;
  background: linear-gradient(var(--vp-local-search-result-bg), transparent);
  z-index: 1000;
}

.result.selected .titles,
.result.selected .title-icon {
  color: var(--vp-c-brand-1) !important;
}

.no-results {
  font-size: 0.9rem;
  text-align: center;
  padding: 12px;
}

svg {
  flex: none;
}

kbd {
  @apply font-mono;
}

[class^=vpi-]:not(.bg),[class*=" vpi-"]:not(.bg),.vp-icon:not(.bg) {
    -webkit-mask: var(--icon) no-repeat;
    mask: var(--icon) no-repeat;
    -webkit-mask-size: 100% 100%;
    mask-size: 100% 100%;
    background-color: currentColor;
    color: inherit
}

[class^=vpi-],[class*=" vpi-"],.vp-icon {
    width: 1em;
    height: 1em
}

.vpi-arrow-right,.vpi-arrow-down,.vpi-arrow-left,.vpi-arrow-up {
    --icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' stroke='currentColor' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' viewBox='0 0 24 24'%3E%3Cpath d='M5 12h14M12 5l7 7-7 7'/%3E%3C/svg%3E")
}

.vpi-chevron-right,.vpi-chevron-down,.vpi-chevron-left,.vpi-chevron-up {
    --icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' stroke='currentColor' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' viewBox='0 0 24 24'%3E%3Cpath d='m9 18 6-6-6-6'/%3E%3C/svg%3E")
}

.vpi-chevron-down,.vpi-arrow-down {
    transform: rotate(90deg)
}

.vpi-chevron-left,.vpi-arrow-left {
    transform: rotate(180deg)
}

.vpi-chevron-up,.vpi-arrow-up {
    transform: rotate(-90deg)
}

.vpi-search {
    --icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' stroke='currentColor' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' viewBox='0 0 24 24'%3E%3Ccircle cx='11' cy='11' r='8'/%3E%3Cpath d='m21 21-4.3-4.3'/%3E%3C/svg%3E")
}

.vpi-layout-list {
    --icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' stroke='currentColor' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' viewBox='0 0 24 24'%3E%3Crect width='7' height='7' x='3' y='3' rx='1'/%3E%3Crect width='7' height='7' x='3' y='14' rx='1'/%3E%3Cpath d='M14 4h7M14 9h7M14 15h7M14 20h7'/%3E%3C/svg%3E")
}

.vpi-delete {
    --icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' stroke='currentColor' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' viewBox='0 0 24 24'%3E%3Cpath d='M20 5H9l-7 7 7 7h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2ZM18 9l-6 6M12 9l6 6'/%3E%3C/svg%3E")
}

.vpi-corner-down-left {
    --icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' stroke='currentColor' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' viewBox='0 0 24 24'%3E%3Cpath d='m9 10-5 5 5 5'/%3E%3Cpath d='M20 4v7a4 4 0 0 1-4 4H4'/%3E%3C/svg%3E")
}

:deep(.excerpt) * {
  font-size: .9rem !important;
  line-height: 130% !important;
  margin: 8px 0px;
}

:deep(.vp-doc) div[class*=language-], .vp-block {
  border-radius: 8px;
  margin: 16px 0;
}
:deep(.vp-doc) div[class*=language-], .vp-block {
  position: relative;
  padding: 8px;
  background-color: var(--vp-code-block-bg);
  overflow-x: auto;
  transition: background-color .5s;
}

:deep(.vp-doc) div[class*=language-] button.copy {
  display: none;
}

:deep(.vp-doc) ul {
  list-style: disc;
}

:deep(.vp-doc) ol {
    list-style: decimal;
}

:deep(.vp-doc) ul, :deep(.vp-doc) ol {
    padding-left: 1.25rem;
    margin: 16px 0;
}

:deep(.vp-doc) li>ol,:deep(.vp-doc) li>ul {
    margin: 8px 0 0
}

:deep(.vp-doc) table {
    display: block;
    border-collapse: collapse;
    margin: 20px 0;
    overflow-x: auto
}

:deep(.vp-doc) tr {
    background-color: var(--vp-c-bg);
    border-top: 1px solid var(--vp-c-divider);
    transition: background-color .5s
}

:deep(.vp-doc) tr:nth-child(2n) {
    background-color: var(--vp-c-bg-soft)
}

:deep(.vp-doc) th,:deep(.vp-doc) td {
    border: 1px solid var(--vp-c-divider);
    padding: 8px 16px
}

:deep(.vp-doc) th {
    text-align: left;
    font-size: 14px;
    font-weight: 600;
    color: var(--vp-c-text-2);
    background-color: var(--vp-c-bg-soft)
}

:deep(.vp-doc) td {
    font-size: 14px
}

:deep(.vp-doc) hr {
    margin: 16px 0;
    border: none;
    border-top: 1px solid var(--vp-c-divider)
}

:deep(.vp-doc) a {
  color: var(--vp-c-brand-1);
  text-decoration: none;
}

:deep(.vp-doc) a:hover {
  text-decoration: underline dotted;
}

:deep(.vp-doc) .titles mark,.excerpt mark {
    background-color: var(--vp-local-search-highlight-bg);
    color: var(--vp-local-search-highlight-text);
    border-radius: 2px;
    padding: 0 2px
}

</style>
